import React, {useState} from 'react';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import PropTypes from 'prop-types';
import TableItem from './TableItem';
import {Box, Typography, Skeleton, Paper, Button, Checkbox, Toolbar, Chip, Dialog, DialogTitle, DialogContent, DialogActions} from '@mui/material';
import {styled} from '@mui/material/styles';
import {ArrowUpward, ArrowDownward, PersonRemove as UnassignIcon} from '@mui/icons-material';
import jwtAxios from '@crema/services/auth/jwt-auth';
import {useDispatch} from 'react-redux';
import {fetchError, showMessage} from 'redux/actions';

// Enhanced styled components
const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  // Enable scrolling within the table area
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: 800, // Increased height for better visibility
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 600,
      fontSize: '14px',
      padding: '16px',
      borderBottom: 'none',
      '&:first-of-type': {
        borderTopLeftRadius: 12,
        paddingLeft: 24,
      },
      '&:last-of-type': {
        borderTopRightRadius: 12,
        paddingRight: 24,
      },
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.grey[50],
      },
    },
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const LoadingSkeleton = () => (
  <TableBody>
    {[...Array(5)].map((_, index) => (
      <TableRow key={index}>
        {[...Array(14)].map((_, cellIndex) => (
          <TableCell key={cellIndex}>
            <Skeleton variant="text" width="100%" height={20} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </TableBody>
);

const DataTable = ({customerData, customerId, onDelete, loading = false}) => {
  const dispatch = useDispatch();
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  
  // Enhanced column headers with sorting - requested order
  const columns = [
    { key: 'id', label: 'ID', minWidth: 90, sortable: true },
    { key: 'fullName', label: 'Full Name', minWidth: 180, sortable: true },
    { key: 'age', label: 'Age', minWidth: 80, sortable: true },
    { key: 'email', label: 'Email', minWidth: 220, sortable: true },
    { key: 'country', label: 'Country', minWidth: 110, sortable: true },
    { key: 'marks', label: 'Marks', minWidth: 100, sortable: true },
    { key: 'MPC_4', label: 'MPC_4', minWidth: 120, sortable: true },
    { key: 'data_source', label: 'Data Source', minWidth: 130, sortable: true },
    { key: 'other_data', label: 'Other Data', minWidth: 160, sortable: false },
    { key: 'integration_source', label: 'Integration Source', minWidth: 160, sortable: true },
    { key: 'sent_at', label: 'Sent At', minWidth: 160, sortable: true },
    { key: 'created_at', label: 'Created At', minWidth: 160, sortable: true },
    { key: 'updated_at', label: 'Updated At', minWidth: 160, sortable: true },
  ];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  // Checkbox handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = customerData.map(data => data.id);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(rowId => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleBulkUnassign = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmUnassign = async () => {
    if (selectedRows.length > 0) {
      setUnassigning(true);
      try {
        // Make API call to unassign data from customer
        await jwtAxios.delete(`data/${customerId}`, {
          data: { dataIds: selectedRows }
        });
        
        // Show success message
        dispatch(showMessage(`Successfully unassigned ${selectedRows.length} data record${selectedRows.length > 1 ? 's' : ''} from customer`));
        
        // Call the parent onDelete callback to update the UI
        if (onDelete) {
          selectedRows.forEach(id => onDelete(id));
        }
        
        // Clear selection and close dialog
        setSelectedRows([]);
        setConfirmDialogOpen(false);
      } catch (error) {
        console.error('Error unassigning data:', error);
        dispatch(fetchError(error.response?.data?.message || error.message || 'Failed to unassign data from customer'));
      } finally {
        setUnassigning(false);
      }
    }
  };

  const handleCancelUnassign = () => {
    setConfirmDialogOpen(false);
  };

  const isAllSelected = customerData.length > 0 && selectedRows.length === customerData.length;
  const isSomeSelected = selectedRows.length > 0 && selectedRows.length < customerData.length;

  // Empty state component
  const renderEmptyState = () => (
    <EmptyState>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No data found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {loading ? 'Loading data...' : 'No customer data available. Try adjusting your filters or add new data.'}
      </Typography>
    </EmptyState>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Bulk Action Toolbar */}
      {selectedRows.length > 0 && (
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            mb: 2,
            borderRadius: 2,
            bgcolor: 'primary.lighter',
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        >
          <Box sx={{ flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`${selectedRows.length} selected`}
              color="primary"
              sx={{ fontWeight: 600 }}
            />
            <Typography variant="body2" color="text.secondary">
              {selectedRows.length === 1 ? '1 item selected' : `${selectedRows.length} items selected`}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="warning"
            startIcon={<UnassignIcon />}
            onClick={handleBulkUnassign}
            sx={{ fontWeight: 600 }}
          >
            Unassign Selected
          </Button>
        </Toolbar>
      )}

      {/* Table Container with horizontal + vertical scroll and sticky header */}
      <Box sx={{ width: '100%' }}>
        <StyledTableContainer>
          <StyledTable stickyHeader sx={{ minWidth: 1200 }}>
        <TableHead>
              <TableRow>
                {/* Checkbox Column */}
                <TableCell
                  padding="checkbox"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    borderBottom: 'none',
                    borderTopLeftRadius: 12,
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isSomeSelected}
                    onChange={handleSelectAll}
                    sx={{
                      color: 'primary.contrastText',
                      '&.Mui-checked': { color: 'primary.contrastText' },
                      '&.MuiCheckbox-indeterminate': { color: 'primary.contrastText' },
                    }}
                  />
                </TableCell>
                {columns.map((column, index) => (
                  <TableCell 
                    key={column.key}
                    sx={{ 
                      minWidth: column.minWidth,
                      fontWeight: 600,
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: '14px',
                      padding: '16px',
                      borderBottom: 'none',
                      cursor: column.sortable ? 'pointer' : 'default',
                      whiteSpace: 'nowrap',
                      '&:hover': column.sortable ? {
                        backgroundColor: 'primary.dark',
                      } : {},
                      ...(index === columns.length - 1 && {
                        borderTopRightRadius: 12,
                        paddingRight: 24,
                      })
                    }}
                    onClick={column.sortable ? () => handleSort(column.key) : undefined}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {column.label}
                      {column.sortable && getSortIcon(column.key)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
        </TableHead>
        {loading ? (
          <LoadingSkeleton />
        ) : customerData.length === 0 ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length + 1} sx={{ border: 'none', padding: 0 }}>
                {renderEmptyState()}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
        <TableBody>
            {customerData.map((data, index) => (
              <TableItem 
                data={data} 
                key={data.id} 
                customerId={customerId}
                index={index}
                selected={selectedRows.includes(data.id)}
                onSelectRow={handleSelectRow}
              />
          ))}
        </TableBody>
        )}
          </StyledTable>
        </StyledTableContainer>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelUnassign} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Bulk Unassign</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to unassign the selected data from this customer?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action will remove the association between the selected data and this customer. 
            The data will remain in the system but will no longer be associated with this customer.
          </Typography>
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Selected Items ({selectedRows.length}):
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {selectedRows.map(id => {
                const data = customerData.find(d => d.id === id);
                return data ? (
                  <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip label={`ID: ${data.id}`} size="small" color="primary" />
                    <Typography variant="body2">
                      {data.fullName || data.name} - {data.email}
                    </Typography>
                  </Box>
                ) : null;
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUnassign} disabled={unassigning}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUnassign} 
            color="warning" 
            variant="contained"
            disabled={unassigning}
            startIcon={<UnassignIcon />}
          >
            {unassigning ? 'Unassigning...' : `Unassign ${selectedRows.length} Item${selectedRows.length > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataTable;

DataTable.defaultProps = {
  customerData: [],
};

DataTable.propTypes = {
  customerData: PropTypes.array,
  customerId: PropTypes.number,
  onDelete: PropTypes.func,
  loading: PropTypes.bool,
};
